// Port of the logic of TrailBivvy/App/Services/WeatherController.swift: the mountain-aware forecast for the hiker's
// position (at their altitude), and forecasts for other places. A plain observable class (no framework): the UI
// subscribes and reads `current` / `isLoading`.

import type { EpochMs, GeoCoordinate, WeatherForecast } from '../core/models';
import { forecastFreshness } from '../core/domain';
import { distance } from '../core/geo';
import type { KeyValueStore } from '../storage/idb';
import { WeatherLocationPrecision } from './lapseRate';
import type { WeatherResult, WeatherService } from './weatherService';

export interface WeatherControllerOptions {
  service: WeatherService;
  /** App clock (demo time in demo mode). */
  now?: () => EpochMs;
  /**
   * Where the last requested position is remembered (the Swift UserDefaults entry used by background refresh). Pass
   * e.g. `kvStore('prefs')`; without it the position is kept in memory only.
   */
  lastRequestStore?: KeyValueStore<number[]>;
}

export interface LastRequestedPosition {
  coordinate: GeoCoordinate;
  elevation?: number;
}

export class WeatherController {
  /** Moving less than this (m) with a fresh forecast does not refresh it. */
  static readonly refreshDistanceMeters = 2000;
  static readonly lastRequestKey = 'TrailBivvy.weather.lastRequest';

  private readonly service: WeatherService;
  private readonly now: () => EpochMs;
  private readonly lastRequestStore?: KeyValueStore<number[]>;
  private readonly listeners = new Set<(controller: WeatherController) => void>();

  private currentResult?: WeatherResult;
  private loading = false;
  private lastRequestedLocation?: GeoCoordinate;
  private lastRequestValues?: number[];
  private latestRequest = 0;
  private inFlight = 0;

  constructor(options: WeatherControllerOptions) {
    this.service = options.service;
    this.now = options.now ?? Date.now;
    this.lastRequestStore = options.lastRequestStore;
  }

  /** The latest result for the hiker's position. */
  get current(): WeatherResult | undefined {
    return this.currentResult;
  }

  get forecast(): WeatherForecast | undefined {
    return this.currentResult?.forecast;
  }

  get isLoading(): boolean {
    return this.loading;
  }

  /** Calls `listener` whenever `current` or `isLoading` changes; returns the unsubscribe function. */
  subscribe(listener: (controller: WeatherController) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Where the last forecast was requested, rounded exactly as the request sent it (2 decimals; elevation to 10 m).
   * Kept only on this device.
   */
  async lastRequestedPosition(): Promise<LastRequestedPosition | undefined> {
    let values = this.lastRequestValues;
    if (!values && this.lastRequestStore) {
      try {
        values = await this.lastRequestStore.get(WeatherController.lastRequestKey);
      } catch {
        values = undefined;
      }
    }
    if (!Array.isArray(values) || values.length < 2) return undefined;
    if (!Number.isFinite(values[0]) || !Number.isFinite(values[1])) return undefined;
    const position: LastRequestedPosition = { coordinate: { latitude: values[0], longitude: values[1] } };
    if (values.length > 2 && Number.isFinite(values[2])) position.elevation = values[2];
    return position;
  }

  /** Refreshes the forecast if the location moved ≥ 2 km or the data is no longer fresh. */
  async refreshIfNeeded(location: GeoCoordinate, elevation?: number): Promise<void> {
    const last = this.lastRequestedLocation;
    const forecast = this.currentResult?.forecast;
    if (
      last &&
      distance(last, location) < WeatherController.refreshDistanceMeters &&
      forecast &&
      forecastFreshness(forecast, this.now()) === 'fresh'
    ) {
      return;
    }
    await this.refresh(location, elevation, false);
  }

  async refresh(location: GeoCoordinate, elevation: number | undefined, force: boolean): Promise<void> {
    this.latestRequest += 1;
    const request = this.latestRequest;
    this.inFlight += 1;
    this.setLoading(true);
    try {
      this.lastRequestedLocation = location;
      this.storeLastRequest(location, elevation);
      const result = await this.service.forecast(location, elevation, force);
      // A slower response for an older position must not replace a newer one.
      if (request === this.latestRequest) {
        this.currentResult = result;
        this.notify();
      }
    } finally {
      this.inFlight -= 1;
      this.setLoading(this.inFlight > 0);
    }
  }

  /** Forecast for another place (e.g. an overnight option at its elevation); cache first when offline. */
  forecastFor(location: GeoCoordinate, elevation?: number): Promise<WeatherResult> {
    return this.service.forecast(location, elevation, false);
  }

  private storeLastRequest(location: GeoCoordinate, elevation: number | undefined): void {
    const rounded = WeatherLocationPrecision.rounded(location);
    const values = [rounded.latitude, rounded.longitude];
    const roundedElevation = WeatherLocationPrecision.roundedElevation(elevation);
    if (roundedElevation !== undefined) values.push(roundedElevation);
    this.lastRequestValues = values;
    this.lastRequestStore?.set(WeatherController.lastRequestKey, values).catch(() => undefined);
  }

  private setLoading(value: boolean): void {
    if (value === this.loading) return;
    this.loading = value;
    this.notify();
  }

  private notify(): void {
    for (const listener of [...this.listeners]) {
      try {
        listener(this);
      } catch (error) {
        console.error('Weather listener failed', error);
      }
    }
  }
}
